/**
 * Comparison transform — derives an in-panel `ComparisonSet` from a fully
 * resolved `AnalysisResponse`. Pure, deterministic, framework-agnostic.
 * Lives in `shared/` so both the server route handler AND the client
 * `ComparisonPage` can call it: the client derives the set from a cached
 * `AnalysisResponse` (avoiding a duplicate `runAnalysis` round-trip), the
 * server still calls it from the export endpoints.
 *
 * Implements PRD §10.3.2 (COMP-07..20). Hard invariants from §10.3.3:
 *   - Pure / deterministic: same `AnalysisResponse` + same thresholds → same
 *     `ComparisonSet`. No external fetches, no DB writes.
 *   - The active confidence floor and partner search filter from the source
 *     panel propagate via the `runAnalysis` filters; we never re-relax them
 *     here. Callers may pass `hubThreshold` / `promiscuityDegreeFloor`
 *     overrides but those are local to the comparison session.
 *
 * Snapshot-diff mode (COMP-01..06) is intentionally not implemented yet — it
 * depends on Stage 1 `ResultSnapshot`s. The `mode` field is set so the same
 * envelope generalises later.
 */

import type {
  AnalysisResponse,
  BridgePartner,
  ComparisonEdge,
  ComparisonSet,
  ComparisonSummary,
  HubPartner,
  InteractionCandidate,
  SharedPartner,
  UniqueBlock,
} from "./schema.js";

export interface ComparisonOptions {
  /** Override the COMP-13 hub threshold; defaults to ceil(0.5 × n_query_genes)
   *  with a floor of 2. */
  hubThreshold?: number;
  /** Override the COMP-17 promiscuity floor; default 50. */
  promiscuityDegreeFloor?: number;
}

const DEFAULT_PROMISCUITY_FLOOR = 50;

function computeHubThreshold(nQueryGenes: number, override?: number): number {
  if (override != null && override >= 2) return override;
  return Math.max(2, Math.ceil(nQueryGenes * 0.5));
}

function toScore1000(combined?: number): number {
  return Math.round((combined ?? 0) * 1000);
}

function toEdge(c: InteractionCandidate): ComparisonEdge {
  return {
    queryGene: c.sourceGene,
    partnerGene: c.partnerGene,
    evidenceTier: c.evidenceTier,
    combinedScore: c.scores.combined ?? 0,
    combinedScore1000: toScore1000(c.scores.combined),
    labPriorityScore: c.labPriority?.score,
    uncertainty: c.uncertainty,
    interactionId: c.id,
  };
}

function avgScore1000(edges: ComparisonEdge[]): number {
  if (edges.length === 0) return 0;
  const sum = edges.reduce((acc, e) => acc + e.combinedScore1000, 0);
  return Math.round(sum / edges.length);
}

/**
 * Group interactions by partner gene and return the list of source query
 * genes that interact with that partner. The query-gene set is taken from
 * `queryGenes` (the original input list) to keep ordering stable.
 */
function indexByPartner(
  interactions: InteractionCandidate[],
  queryGenesSet: Set<string>,
): Map<string, InteractionCandidate[]> {
  const byPartner = new Map<string, InteractionCandidate[]>();
  for (const c of interactions) {
    // Skip degenerate self-edges (a query gene listed as its own partner) and
    // edges where the source is not in the canonical query gene list — the
    // STRING adapter occasionally returns swapped tuples.
    const sourceInQuery = queryGenesSet.has(c.sourceGene);
    if (!sourceInQuery) continue;
    if (c.sourceGene === c.partnerGene) continue;
    const list = byPartner.get(c.partnerGene) ?? [];
    list.push(c);
    byPartner.set(c.partnerGene, list);
  }
  return byPartner;
}

/**
 * Build a (queryGeneA → Set<queryGeneB>) adjacency map for query-gene pairs
 * that interact directly above the active confidence threshold. Used by
 * COMP-14 to identify which pairs need bridging vs which already touch.
 */
function buildQueryGeneAdjacency(
  interactions: InteractionCandidate[],
  queryGenesSet: Set<string>,
): Map<string, Set<string>> {
  const adj = new Map<string, Set<string>>();
  for (const c of interactions) {
    if (!queryGenesSet.has(c.sourceGene) || !queryGenesSet.has(c.partnerGene)) continue;
    if (c.sourceGene === c.partnerGene) continue;
    const a = adj.get(c.sourceGene) ?? new Set<string>();
    a.add(c.partnerGene);
    adj.set(c.sourceGene, a);
    const b = adj.get(c.partnerGene) ?? new Set<string>();
    b.add(c.sourceGene);
    adj.set(c.partnerGene, b);
  }
  return adj;
}

export function buildComparisonSet(
  response: AnalysisResponse,
  queryGenes: string[],
  opts: ComparisonOptions = {},
): ComparisonSet {
  if (queryGenes.length < 2) {
    throw new Error("comparison_requires_two_or_more_query_genes");
  }

  const queryGenesSet = new Set(queryGenes);
  const promiscuityFloor =
    opts.promiscuityDegreeFloor ?? DEFAULT_PROMISCUITY_FLOOR;
  const hubThreshold = computeHubThreshold(queryGenes.length, opts.hubThreshold);

  const byPartner = indexByPartner(response.interactions, queryGenesSet);
  const queryAdjacency = buildQueryGeneAdjacency(response.interactions, queryGenesSet);

  // Partition partners into shared (touches ≥2 query genes) vs unique-per-gene.
  const shared: SharedPartner[] = [];
  const uniqueByGene = new Map<string, ComparisonEdge[]>();
  for (const g of queryGenes) uniqueByGene.set(g, []);

  for (const [partnerGene, candidates] of byPartner.entries()) {
    // Keep at most one edge per (queryGene, partner) — pick the highest-scoring
    // direction if STRING duplicated the pair.
    const bestPerSource = new Map<string, InteractionCandidate>();
    for (const c of candidates) {
      const prev = bestPerSource.get(c.sourceGene);
      if (!prev || (c.scores.combined ?? 0) > (prev.scores.combined ?? 0)) {
        bestPerSource.set(c.sourceGene, c);
      }
    }

    // Stable ordering: by the original queryGenes input order.
    const orderedSources = queryGenes.filter((g) => bestPerSource.has(g));
    const edges = orderedSources.map((g) => toEdge(bestPerSource.get(g)!));
    const partnerUniprotId = candidates.find((c) => !!c.partnerUniprotId)?.partnerUniprotId;

    if (edges.length >= 2) {
      shared.push({
        partnerGene,
        partnerUniprotId,
        queryGenes: orderedSources,
        nQueryGenes: edges.length,
        avgScore1000: avgScore1000(edges),
        edges,
      });
    } else if (edges.length === 1) {
      const sourceGene = orderedSources[0];
      const bucket = uniqueByGene.get(sourceGene);
      if (bucket) bucket.push(edges[0]);
    }
  }

  // Stable ordering for shared: avgScore desc, then nQueryGenes desc, then
  // partner gene asc for deterministic re-derivation.
  shared.sort((a, b) => {
    if (b.avgScore1000 !== a.avgScore1000) return b.avgScore1000 - a.avgScore1000;
    if (b.nQueryGenes !== a.nQueryGenes) return b.nQueryGenes - a.nQueryGenes;
    return a.partnerGene.localeCompare(b.partnerGene);
  });

  // Unique blocks — one per query gene, edges sorted by score desc.
  const unique: UniqueBlock[] = queryGenes.map((g) => {
    const edges = (uniqueByGene.get(g) ?? []).slice().sort((a, b) => b.combinedScore1000 - a.combinedScore1000);
    return { queryGene: g, count: edges.length, edges };
  });

  // Hubs (COMP-13): subset of shared with nQueryGenes ≥ hubThreshold. Rank
  // by the same comparator as shared.sort above.
  const hubs: HubPartner[] = shared
    .filter((s) => s.nQueryGenes >= hubThreshold)
    .map((s, idx) => {
      // STRING degree is not yet persisted alongside the candidate (Stage 0.6
      // candidate to wire the actual degree fetch). Use -1 as the sentinel
      // and only flag promiscuity when we have a real number to compare.
      const degree = -1;
      const promiscuityRisk =
        degree >= 0 ? degree >= promiscuityFloor : false;
      return {
        ...s,
        rank: idx + 1,
        degree,
        promiscuityRisk,
      };
    });

  // Bridges (COMP-14): partners that touch ≥2 query genes whose query-gene
  // pairs are NOT directly adjacent (above the active confidence floor).
  const bridges: BridgePartner[] = [];
  for (const s of shared) {
    const bridged: Array<[string, string]> = [];
    for (let i = 0; i < s.queryGenes.length; i++) {
      for (let j = i + 1; j < s.queryGenes.length; j++) {
        const a = s.queryGenes[i];
        const b = s.queryGenes[j];
        const aNeighbors = queryAdjacency.get(a);
        if (!aNeighbors || !aNeighbors.has(b)) {
          bridged.push([a, b]);
        }
      }
    }
    if (bridged.length === 0) continue;
    bridges.push({
      rank: 0, // assigned after sort
      partnerGene: s.partnerGene,
      partnerUniprotId: s.partnerUniprotId,
      bridgedPairs: bridged,
      queryGenes: s.queryGenes,
      nQueryGenes: s.nQueryGenes,
      avgScore1000: s.avgScore1000,
      edges: s.edges,
    });
  }
  bridges.sort((a, b) => {
    if (b.avgScore1000 !== a.avgScore1000) return b.avgScore1000 - a.avgScore1000;
    if (b.bridgedPairs.length !== a.bridgedPairs.length)
      return b.bridgedPairs.length - a.bridgedPairs.length;
    return a.partnerGene.localeCompare(b.partnerGene);
  });
  bridges.forEach((br, i) => {
    br.rank = i + 1;
  });

  const summary: ComparisonSummary = {
    totalInteractions: response.interactions.length,
    sharedCount: shared.length,
    uniqueCount: unique.reduce((acc, u) => acc + u.count, 0),
    hubsCount: hubs.length,
    bridgesCount: bridges.length,
    hubThreshold,
    promiscuityDegreeFloor: promiscuityFloor,
  };

  return {
    mode: "in_panel",
    queryRunId: response.queryRunId,
    queryGenes,
    species: response.species,
    context: response.context,
    summary,
    shared,
    unique,
    hubs,
    bridges,
    meta: {
      // Inherit version metadata from the source AnalysisResponse so the
      // ComparisonSet is bound to the same KB / scoring snapshot — important
      // for the export envelope (REPR-03).
      appVersion: response.meta.appVersion,
      kbVersion: response.meta.kbVersion,
      scoringConfigVersion: response.meta.scoringConfigVersion,
      generatedAt: Date.now(),
    },
  };
}
